'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { FglHeader } from '@/components/layout/fgl-header'
import { GliderBackdrop, GliderSwarm, LivingThermal, Reveal } from '@/components/award'
import { MapDisclaimer } from '@/components/legal/map-disclaimer'
import { ExternalLink } from '@/components/legal/external-link'
import { useTranslation } from '@/i18n/client'

// MapLibre lazy laden
const FlyingFieldsMap = dynamic(() => import('@/components/live/flying-fields-map'), { ssr: false })
// r19.55.225 (2026-08-14): Cooperative-Touch + Vollbild-Button für Handy/Tablet
const MobileMapWrapper = dynamic(
  () => import('@/components/maps/mobile-map-wrapper').then(m => m.MobileMapWrapper),
  { ssr: false },
)

interface FlyingField {
  id: string
  name: string
  slug: string
  lat: number
  lon: number
  radius_m: number
  altitude_m: number | null
  country: string | null
  region: string | null
  description: string | null
  rules_text: string | null
  website_url: string | null
  icao_zone: string | null
  airspace_ceiling_ft: number | null
  dipul_url: string | null
  verified: boolean
}

// Kein Cookie-Bypass mehr — Disclaimer wird IMMER bei jedem Aufruf angezeigt
export default function FlyingFieldsPage() {
  const { t } = useTranslation()
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false)

  const handleDisclaimerAccept = useCallback(() => {
    setDisclaimerAccepted(true)
  }, [])
  const [fields, setFields] = useState<FlyingField[]>([])
  const [selectedField, setSelectedField] = useState<FlyingField | null>(null)
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)

  // Filter + Suche
  const [allFields, setAllFields] = useState<any[]>([])
  const [terrainFilter, setTerrainFilter] = useState<'all' | 'slope' | 'flatland'>('all')
  const [countryFilter, setCountryFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [countryCounts, setCountryCounts] = useState<{ code: string; count: number; slopeCount: number }[]>([])
  const [mapFlyTo, setMapFlyTo] = useState<{ lat: number; lon: number; zoom: number } | null>(null)

  useEffect(() => {
    // Alle Flugplätze als GeoJSON laden (für Suche + Filter)
    fetch('/api/flying-fields/geojson')
      .then(r => r.json())
      .then(data => {
        const features = data.features || []
        setAllFields(features)

        // Länder-Statistik
        const stats: Record<string, { count: number; slopeCount: number }> = {}
        features.forEach((f: any) => {
          const cc = f.properties?.country || '??'
          if (!stats[cc]) stats[cc] = { count: 0, slopeCount: 0 }
          stats[cc].count++
          if (f.properties?.terrain === 'slope') stats[cc].slopeCount++
        })
        const sorted = Object.entries(stats)
          .map(([code, s]) => ({ code, count: s.count, slopeCount: s.slopeCount }))
          .sort((a, b) => b.count - a.count)
        setCountryCounts(sorted)

        // Hash-Anker wird im separaten useEffect verarbeitet (nach Disclaimer)
      })
      .catch(() => {})
  }, [])

  // Hash-Anker: Nach Disclaimer-Akzeptieren zum gewählten Flugfeld zoomen
  useEffect(() => {
    if (!disclaimerAccepted || allFields.length === 0) return
    if (typeof window === 'undefined' || !window.location.hash) return
    const fieldName = decodeURIComponent(window.location.hash.slice(1))
    if (!fieldName) return
    const q = fieldName.toLowerCase()
    const match = allFields.find((f: any) => {
      const name = (f.properties?.name || '').toLowerCase()
      return name === q || name.includes(q) || q.includes(name)
    })
    if (match?.geometry?.coordinates) {
      const [lon, lat] = match.geometry.coordinates
      // Map braucht Zeit zum Rendern nach Disclaimer-Accept — 2s Delay
      setTimeout(() => setMapFlyTo({ lat, lon, zoom: 14 }), 2000)
    }
  }, [disclaimerAccepted, allFields])

  // Suche
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return }
    const q = searchQuery.toLowerCase()
    const results = allFields
      .filter((f: any) => {
        const name = (f.properties?.name || '').toLowerCase()
        const matchTerrain = terrainFilter === 'all' || f.properties?.terrain === terrainFilter
        const matchCountry = !countryFilter || f.properties?.country === countryFilter
        return name.includes(q) && matchTerrain && matchCountry
      })
      .slice(0, 15)
    setSearchResults(results)
    setShowSearchDropdown(true)
  }, [searchQuery, allFields, terrainFilter, countryFilter])

  const handleFlyToField = (feature: any) => {
    const [lon, lat] = feature.geometry.coordinates
    setMapFlyTo({ lat, lon, zoom: 14 })
    setSearchQuery(feature.properties.name)
    setShowSearchDropdown(false)
  }

  const handleFlyToCountry = (cc: string) => {
    // Feste Landeskoordinaten + passender Zoom
    const countryGeo: Record<string, { lat: number; lon: number; zoom: number }> = {
      DE: { lat: 51.2, lon: 10.4, zoom: 6 },
      AT: { lat: 47.5, lon: 13.5, zoom: 7 },
      CH: { lat: 46.8, lon: 8.2, zoom: 8 },
      FR: { lat: 46.6, lon: 2.5, zoom: 6 },
      IT: { lat: 42.5, lon: 12.5, zoom: 6 },
      ES: { lat: 40.0, lon: -3.7, zoom: 6 },
      PT: { lat: 39.5, lon: -8.0, zoom: 7 },
      NL: { lat: 52.2, lon: 5.3, zoom: 8 },
      BE: { lat: 50.5, lon: 4.4, zoom: 8 },
      PL: { lat: 52.0, lon: 19.5, zoom: 6 },
      CZ: { lat: 49.8, lon: 15.5, zoom: 7 },
      SK: { lat: 48.7, lon: 19.7, zoom: 8 },
      HU: { lat: 47.2, lon: 19.5, zoom: 7 },
      DK: { lat: 56.0, lon: 10.0, zoom: 7 },
      SE: { lat: 62.0, lon: 15.0, zoom: 5 },
      NO: { lat: 64.0, lon: 12.0, zoom: 5 },
      FI: { lat: 64.0, lon: 26.0, zoom: 5 },
      IE: { lat: 53.4, lon: -7.9, zoom: 7 },
      UK: { lat: 54.0, lon: -2.5, zoom: 6 },
      GB: { lat: 54.0, lon: -2.5, zoom: 6 },
      US: { lat: 39.8, lon: -98.5, zoom: 4 },
      CA: { lat: 56.0, lon: -96.0, zoom: 4 },
      BR: { lat: -14.2, lon: -51.9, zoom: 4 },
      AU: { lat: -25.3, lon: 133.8, zoom: 4 },
      NZ: { lat: -41.3, lon: 174.8, zoom: 6 },
      ZA: { lat: -30.6, lon: 25.0, zoom: 6 },
      MX: { lat: 23.6, lon: -102.5, zoom: 5 },
      ID: { lat: -2.5, lon: 118.0, zoom: 5 },
      VE: { lat: 7.0, lon: -66.0, zoom: 6 },
      EU: { lat: 50.0, lon: 10.0, zoom: 5 },
      XX: { lat: 30.0, lon: 0.0, zoom: 3 },
    }

    const geo = countryGeo[cc]
    if (geo) {
      setMapFlyTo({ lat: geo.lat, lon: geo.lon, zoom: geo.zoom })
    } else {
      // Fallback: Berechne aus Plätzen
      const countryFields = allFields.filter((f: any) => f.properties?.country === cc)
      if (countryFields.length === 0) return
      const lats = countryFields.map((f: any) => f.geometry.coordinates[1])
      const lons = countryFields.map((f: any) => f.geometry.coordinates[0])
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2
      const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2
      setMapFlyTo({ lat: centerLat, lon: centerLon, zoom: 6 })
    }
    setCountryFilter(cc === countryFilter ? '' : cc)
  }

  useEffect(() => {
    fetch('/api/flying-fields')
      .then(r => r.json())
      .then(data => { setFields(data.fields || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  return (
    // Outer-Container: relative + min-h-screen + opaker BG, damit GliderBackdrop's
    // absolute-positionierter z=-1-Layer die volle Seitenhoehe ausfuellt.
    // Vorher war die BG am inneren disclaimerAccepted-Wrapper, der die Segler verdeckt
    // hat. Bug-Fix 2026-05-09: BG nach aussen + relative-Container.
    <div data-award="true" className="relative min-h-screen" style={{ background: 'var(--award-ink)' }}>
    {!disclaimerAccepted && <MapDisclaimer onAccept={handleDisclaimerAccept} type="2d" />}
    <FglHeader />
      <GliderBackdrop />
    {disclaimerAccepted && <div>
      {/* HERO — Award-Pattern wie auf Features/FAQ/Donate. Yellow-Warnbanner 2026-05-09 entfernt,
          rechtlicher Disclaimer steht jetzt fett im Lead-Text + ausfuehrlich im Accordion darunter. */}
      <section className="relative pt-[110px] pb-6 md:pt-[140px] md:pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <LivingThermal />
        <div className="absolute inset-0 award-noise pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="award-eyebrow">Fluggebiete · Weltweit</span>
              <span className="h-px w-12" style={{ background: 'var(--award-line-hi)' }} />
              <span className="award-label">{allFields.length > 0 ? `${allFields.length.toLocaleString('de-DE')} Plätze` : '11.000+ Plätze · ICAO · NOTAM'}</span>
            </div>
          </Reveal>
          <div className="grid grid-cols-12 gap-4 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={1}>
                <h1 className="award-h-mega" style={{ marginBottom: 0 }}>
                  Fluggebiete <span className="award-grad-text">weltweit.</span>
                </h1>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-5">
              <Reveal delay={2}>
                <p className="text-base md:text-lg font-bold leading-relaxed" style={{ color: 'var(--award-cirrus)' }}>
                  Modellflugplätze und Hangflug-Gelände auf der Karte. Zur ersten
                  Orientierung. Keine amtliche Luftfahrtinformation. Keine Flugfreigabe.
                  Verantwortung stets beim Piloten.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto p-6">
        {/* Rechtlicher Hinweis — als Accordion */}
        <details className="rounded-2xl mb-4" style={{ background: 'var(--bg-card)', border: '2px solid #ef4444' }}>
          <summary className="flex items-center gap-3 p-4 cursor-pointer select-none">
            <span className="text-xl flex-shrink-0">⚠️</span>
            <h3 className="font-black text-sm uppercase tracking-wide flex-1" style={{ color: '#ef4444' }}>
              {t('flyingFields.disclaimerTitle')}
            </h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#ef444420', color: '#ef4444' }}>{t('flyingFields.requiredReading')}</span>
          </summary>
          <div className="px-4 pb-4">
            <div>

              <div className="space-y-3 mt-3 text-sm" style={{ color: 'var(--muted)' }}>
                <p>
                  {t('flyingFields.airspaceData')}: <ExternalLink href="https://www.openaip.net/" className="font-bold hover:underline" style={{ color: 'var(--accent)' }}>OpenAIP</ExternalLink> (Community).
                  <strong style={{ color: 'var(--text)' }}> {t('flyingFields.noNotams')}</strong> {t('flyingFields.roughOrientation')}
                </p>

                <div className="p-3 rounded-xl text-xs" style={{ background: 'var(--bg-primary)', border: '1px solid #ef4444' }}>
                  <p className="font-black uppercase tracking-wide text-[10px] mb-1.5" style={{ color: '#ef4444' }}>{t('flyingFields.disclaimerLabel')}</p>
                  <p style={{ color: 'var(--muted)' }}>
                    {t('flyingFields.disclaimerText1')}
                  </p>
                  <p className="mt-1.5" style={{ color: 'var(--muted)' }}>
                    {t('flyingFields.disclaimerText2')}
                  </p>
                  <p className="mt-1.5 font-bold" style={{ color: '#ef4444' }}>
                    {t('flyingFields.disclaimerText3')}
                  </p>
                </div>

                {/* Accordion: NOTAM-Portale */}
                <details>
                  <summary className="text-xs font-bold cursor-pointer" style={{ color: 'var(--accent)' }}>
                    {t('flyingFields.notamPortals')}
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      { flag: '🇩🇪', label: 'DFS AIS NOTAMs', url: 'https://secais.dfs.de/pilotservice/briefing/notam/notam_edit_text.jsp', primary: true },
                      { flag: '🇦🇹', label: 'Austro Control NOTAMs', url: 'https://www.homebriefing.com/cms-acg/opencms/en/NOTAM_briefing/' },
                      { flag: '🇨🇭', label: 'Skyguide NOTAMs', url: 'https://skybriefing.com/en/services/notam-briefing' },
                      { flag: '🇫🇷', label: 'SOFIA-Briefing (SIA)', url: 'https://sofia-briefing.aviation-civile.gouv.fr/sofia/pages/homepage.html' },
                      { flag: '🇬🇧', label: 'NATS AIS Briefing', url: 'https://nats-uk.ead-it.com/cms-nats/opencms/en/home/' },
                      { flag: '🇮🇹', label: 'ENAV Online Services', url: 'https://www.enav.it/en/online-services' },
                      { flag: '🇪🇸', label: 'ENAIRE NOTAMs', url: 'https://aip.enaire.es/aip/NOTAM-en.html' },
                      { flag: '🇺🇸', label: 'FAA NOTAM Search', url: 'https://notams.aim.faa.gov/notamSearch/' },
                      { flag: '🇦🇺', label: 'Airservices Australia', url: 'https://www.airservicesaustralia.com/flight-briefing/' },
                      { flag: '🇪🇺', label: 'Eurocontrol NOP', url: 'https://www.eurocontrol.int/service/network-operations-portal' },
                      { flag: '🌍', label: 'ICAO iSTARS', url: 'https://www.icao.int/safety/iStars/Pages/API-Data-Service.aspx' },
                    ].map((n, i) => (
                      <ExternalLink key={i} href={n.url}
                        className="px-3 py-1.5 rounded-full font-bold text-xs inline-flex items-center gap-1.5"
                        style={n.primary ? { background: '#ef4444', color: '#fff' } : { border: '1px solid var(--border)', color: 'var(--muted)' }}>
                        {n.flag} {n.label}
                      </ExternalLink>
                    ))}
                  </div>
                </details>

                <details className="mt-2">
                  <summary className="text-xs font-bold cursor-pointer" style={{ color: 'var(--accent)' }}>
                    {t('flyingFields.icaoExplanation')}
                  </summary>
                  <div className="mt-3 space-y-3 text-xs" style={{ color: 'var(--muted)' }}>
                    {/* Luftraumstruktur Grafik — Link zu Wikimedia */}
                    <ExternalLink href="https://commons.wikimedia.org/wiki/File:Luftraumstruktur_Deutschland.svg"
                      className="block rounded-xl p-3 hover:opacity-80 transition-opacity"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--accent)' }}>
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">🗺️</span>
                        <div>
                          <p className="font-black text-sm" style={{ color: 'var(--accent)' }}>
                            {t('flyingFields.icaoGraphicTitle')}
                          </p>
                          <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>
                            {t('flyingFields.icaoGraphicSubtitle')}
                          </p>
                        </div>
                        <span style={{ color: 'var(--accent)' }}>→</span>
                      </div>
                    </ExternalLink>

                    <p>{t('flyingFields.icaoIntro')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                        <span className="font-black" style={{ color: '#ef4444' }}>{t('flyingFields.classAD')}</span>
                        <p className="mt-1">{t('flyingFields.classADDesc')}</p>
                      </div>
                      <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                        <span className="font-black" style={{ color: '#f59e0b' }}>{t('flyingFields.classE')}</span>
                        <p className="mt-1">{t('flyingFields.classEDesc')}</p>
                      </div>
                      <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                        <span className="font-black" style={{ color: '#22c55e' }}>{t('flyingFields.classG')}</span>
                        <p className="mt-1">{t('flyingFields.classGDesc')}</p>
                      </div>
                      <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                        <span className="font-black" style={{ color: '#ef4444' }}>{t('flyingFields.specialAreas')}</span>
                        <p className="mt-1">{t('flyingFields.specialAreasDesc')}</p>
                      </div>
                    </div>
                    <p>{t('flyingFields.modelFlightNote')}</p>

                    <p className="font-bold mt-3" style={{ color: 'var(--text)' }}>{t('flyingFields.furtherSources')}</p>
                    <div className="space-y-1.5 mt-1">
                      <ExternalLink href="https://aopa.de/wp-content/uploads/24_ASL_VFR-Luftfahrtkarten.pdf"
                        className="flex items-center gap-2 p-2 rounded-lg hover:opacity-80" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                        <span>📄</span>
                        <div>
                          <span className="font-bold" style={{ color: 'var(--text)' }}>AOPA Safety Letter — VFR-Luftfahrtkarten</span>
                          <span className="block text-[9px]" style={{ color: 'var(--muted)' }}>PDF, deutsch — Alle Kartensymbole und Luftraumklassen kompakt erklärt</span>
                        </div>
                      </ExternalLink>
                      <ExternalLink href="https://www.eisenschmidt.aero/wie-geht-icao-karte"
                        className="flex items-center gap-2 p-2 rounded-lg hover:opacity-80" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                        <span>🗺️</span>
                        <div>
                          <span className="font-bold" style={{ color: 'var(--text)' }}>Eisenschmidt — Wie geht ICAO-Karte?</span>
                          <span className="block text-[9px]" style={{ color: 'var(--muted)' }}>Interaktive Erklärung aller Zeichen, Symbole und Farben auf der ICAO-Karte</span>
                        </div>
                      </ExternalLink>
                      <ExternalLink href="https://commons.wikimedia.org/wiki/File:Luftraumstruktur_Deutschland.svg"
                        className="flex items-center gap-2 p-2 rounded-lg hover:opacity-80" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                        <span>🖼️</span>
                        <div>
                          <span className="font-bold" style={{ color: 'var(--text)' }}>Wikimedia — Luftraumstruktur Deutschland (SVG)</span>
                          <span className="block text-[9px]" style={{ color: 'var(--muted)' }}>Querschnitt-Grafik mit allen Klassen A–G, CTR, TMZ — CC BY-SA 3.0</span>
                        </div>
                      </ExternalLink>
                      <ExternalLink href="https://segelfliegenausbildung.de/index.php/theoretische-spl-ausbildung/9-navigation/9-3-luftfahrtkarten"
                        className="flex items-center gap-2 p-2 rounded-lg hover:opacity-80" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                        <span>🎓</span>
                        <div>
                          <span className="font-bold" style={{ color: 'var(--text)' }}>Segelflug-Ausbildung — Luftfahrtkarten</span>
                          <span className="block text-[9px]" style={{ color: 'var(--muted)' }}>Aus der SPL-Theorieausbildung: Kartentypen, Maßstäbe und Symbole erklärt</span>
                        </div>
                      </ExternalLink>
                    </div>
                  </div>
                </details>

                {/* Zuständige Flugsicherungen weltweit */}
                <details className="mt-2">
                  <summary className="text-xs font-bold cursor-pointer" style={{ color: 'var(--accent)' }}>
                    {t('flyingFields.atcAuthorities')}
                  </summary>
                  <div className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
                    <p className="mb-2">{t('flyingFields.atcIntro')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {[
                        { flag: '🇩🇪', name: 'DFS — Deutsche Flugsicherung', url: 'https://www.dfs.de', country: 'Deutschland' },
                        { flag: '🇦🇹', name: 'Austro Control', url: 'https://www.austrocontrol.at', country: 'Österreich' },
                        { flag: '🇨🇭', name: 'Skyguide', url: 'https://www.skyguide.ch', country: 'Schweiz' },
                        { flag: '🇫🇷', name: 'DGAC / DSNA', url: 'https://www.ecologie.gouv.fr/direction-services-navigation-aerienne-dsna', country: 'Frankreich' },
                        { flag: '🇬🇧', name: 'NATS', url: 'https://www.nats.aero', country: 'Großbritannien' },
                        { flag: '🇮🇹', name: 'ENAV', url: 'https://www.enav.it', country: 'Italien' },
                        { flag: '🇪🇸', name: 'ENAIRE', url: 'https://www.enaire.es', country: 'Spanien' },
                        { flag: '🇳🇱', name: 'LVNL', url: 'https://www.lvnl.nl', country: 'Niederlande' },
                        { flag: '🇧🇪', name: 'skeyes', url: 'https://www.skeyes.be', country: 'Belgien' },
                        { flag: '🇨🇿', name: 'ŘLP ČR', url: 'https://www.rlp.cz', country: 'Tschechien' },
                        { flag: '🇵🇱', name: 'PAŻP (PANSA)', url: 'https://www.pansa.pl', country: 'Polen' },
                        { flag: '🇸🇪', name: 'LFV', url: 'https://www.lfv.se', country: 'Schweden' },
                        { flag: '🇳🇴', name: 'Avinor', url: 'https://www.avinor.no', country: 'Norwegen' },
                        { flag: '🇩🇰', name: 'Naviair', url: 'https://www.naviair.dk', country: 'Dänemark' },
                        { flag: '🇭🇺', name: 'HungaroControl', url: 'https://www.hungarocontrol.hu', country: 'Ungarn' },
                        { flag: '🇺🇸', name: 'FAA', url: 'https://www.faa.gov', country: 'USA' },
                        { flag: '🇨🇦', name: 'NAV CANADA', url: 'https://www.navcanada.ca', country: 'Kanada' },
                        { flag: '🇦🇺', name: 'Airservices Australia', url: 'https://www.airservicesaustralia.com', country: 'Australien' },
                        { flag: '🇧🇷', name: 'DECEA', url: 'https://www.decea.mil.br', country: 'Brasilien' },
                        { flag: '🇿🇦', name: 'ATNS', url: 'https://www.atns.co.za', country: 'Südafrika' },
                        { flag: '🇪🇺', name: 'Eurocontrol', url: 'https://www.eurocontrol.int', country: 'Europa (Koordination)' },
                        { flag: '🌍', name: 'ICAO', url: 'https://www.icao.int', country: 'International (Standards)' },
                      ].map((org, i) => (
                        <ExternalLink key={i} href={org.url}
                          className="flex items-center gap-2 p-2 rounded-lg hover:opacity-80 transition-opacity"
                          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                          <span className="text-base">{org.flag}</span>
                          <div className="min-w-0">
                            <div className="font-bold text-[10px] truncate" style={{ color: 'var(--text)' }}>{org.name}</div>
                            <div className="text-[9px]" style={{ color: 'var(--muted)' }}>{org.country}</div>
                          </div>
                        </ExternalLink>
                      ))}
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </details>

        {/* Filter + Suche */}
        <div className="rounded-2xl p-4 mb-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div className="flex flex-wrap gap-3 items-start">
            {/* Terrain-Filter */}
            <div className="flex gap-1">
              {[
                { key: 'all' as const, label: t('flyingFields.filterAll'), count: allFields.length },
                { key: 'slope' as const, label: t('flyingFields.filterSlope'), count: allFields.filter(f => f.properties?.terrain === 'slope').length, color: '#f97316' },
                { key: 'flatland' as const, label: t('flyingFields.filterFlatland'), count: allFields.filter(f => f.properties?.terrain !== 'slope').length, color: '#3b82f6' },
              ].map(t => (
                <button key={t.key} onClick={() => setTerrainFilter(t.key)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5"
                  style={{
                    background: terrainFilter === t.key ? (t.color || 'var(--accent)') : 'transparent',
                    color: terrainFilter === t.key ? '#fff' : 'var(--muted)',
                    border: `1px solid ${terrainFilter === t.key ? (t.color || 'var(--accent)') : 'var(--border)'}`,
                    opacity: terrainFilter === t.key ? 1 : 0.7,
                  }}>
                  {t.color && <span className="w-2 h-2 rounded-full inline-block" style={{ background: t.color }} />}
                  {t.label} <span className="opacity-60">{t.count}</span>
                </button>
              ))}
            </div>

            {/* Suche */}
            <div className="flex-1 min-w-[250px] relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowSearchDropdown(true)}
                onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                placeholder={t('flyingFields.searchPlaceholder')}
                className="w-full px-3 py-1.5 rounded-lg text-sm"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text)' }}
              />
              {showSearchDropdown && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl max-h-[300px] overflow-y-auto"
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', boxShadow: '0 12px 40px rgba(0,0,0,.5)', backdropFilter: 'none' }}>
                  {searchResults.map((f: any, i: number) => {
                    const p = f.properties
                    return (
                      <button key={i}
                        onMouseDown={() => handleFlyToField(f)}
                        className="w-full text-start px-3 py-2 text-xs hover:bg-[var(--accent)]/8 transition-colors border-b last:border-b-0"
                        style={{ borderColor: 'var(--border)' }}>
                        <span className="font-bold" style={{ color: 'var(--text)' }}>{p.name}</span>
                        <span className="ms-2" style={{ color: 'var(--muted)' }}>
                          {p.country} · {p.terrain === 'slope' ? '🟠 Slope' : '🔵 Flat'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Länder-Buttons */}
          <div className="flex flex-wrap gap-1 mt-3">
            {countryCounts.slice(0, 25).map(cc => (
              <button key={cc.code}
                onClick={() => handleFlyToCountry(cc.code)}
                className="px-2 py-1 rounded-lg text-[9px] font-bold transition-all"
                style={{
                  background: countryFilter === cc.code ? 'var(--accent)' : 'transparent',
                  color: countryFilter === cc.code ? '#000' : 'var(--muted)',
                  border: `1px solid ${countryFilter === cc.code ? 'var(--accent)' : 'var(--border)'}`,
                }}>
                {cc.code} <span className="opacity-50">{cc.count}</span>
                {cc.slopeCount > 0 && <span className="ms-0.5 text-orange-400">{cc.slopeCount}⛰</span>}
              </button>
            ))}
            {countryCounts.length > 25 && (
              <span className="px-2 py-1 text-[9px]" style={{ color: 'var(--muted)' }}>
                {t('flyingFields.moreCountries', { count: countryCounts.length - 25 })}
              </span>
            )}
          </div>
        </div>

        {/* Karte — R103.38: translate="no" schuetzt MapLibre/Cesium-DOM vor
            Browser-Translation-Extensions (DeepL/Google Translate), die Knoten
            ersetzen und damit React + WebGL-Kontext crashen.
            r19.55.225 (2026-08-14): MobileMapWrapper mit Cooperative-Touch. */}
        <div className="mb-6 notranslate" translate="no">
          <MobileMapWrapper
            desktopHeight="700px"
            tabletHeight="65vh"
            mobileHeight="60vh"
            mobileHeightLandscape="80vh"
          >
            <FlyingFieldsMap
              fields={fields}
              onSelectField={setSelectedField}
              selectedField={selectedField}
              flyTo={mapFlyTo}
              terrainFilter={terrainFilter}
            />
          </MobileMapWrapper>
        </div>

        {/* Datenquellen + Flugplatz melden — kompakt */}
        <div className="flex flex-wrap gap-3 mb-6 items-stretch">
          {/* Datenquellen Accordion */}
          <details className="flex-1 min-w-[300px] rounded-2xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <summary className="p-3 cursor-pointer select-none flex items-center gap-2">
              <span className="text-sm">📡</span>
              <span className="text-xs font-bold" style={{ color: 'var(--text)' }}>{t('flyingFields.dataSources')}</span>
              <span className="text-[9px] ms-auto" style={{ color: 'var(--muted)' }}>{t('flyingFields.autoSync')}</span>
            </summary>
            <div className="px-3 pb-3 space-y-2 text-xs" style={{ color: 'var(--muted)' }}>
              <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                <span className="font-bold" style={{ color: 'var(--text)' }}>{t('flyingFields.modelAirfields')}</span>
                <span className="ms-1">{t('flyingFields.airfieldsFrom', { count: allFields.length.toLocaleString() })}</span>
                <p className="text-[10px] mt-0.5">Datenquelle:{' '}
                  <ExternalLink href="https://ma-db.com/" className="font-bold hover:underline" style={{ color: 'var(--accent)' }}>
                    Thomas Ehrhardt &amp; Arno Bertz — ma-db.com
                  </ExternalLink>
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>
                  Auf Basis der weltweiten KMZ-Datei von{' '}
                  <ExternalLink href="https://arnobertz.de/" className="hover:underline" style={{ color: 'var(--accent)' }}>
                    Arno Bertz (arnobertz.de)
                  </ExternalLink>
                  {' '}· mit freundlicher Genehmigung, unbefristete Nutzung
                </p>
              </div>
              <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                <span className="font-bold" style={{ color: 'var(--text)' }}>{t('flyingFields.airspaceDataLabel')}</span>
                <span className="ms-1">{t('flyingFields.airspacesAirportsNavaids')}</span>
                <p className="text-[10px] mt-0.5">Quelle:{' '}
                  <ExternalLink href="https://www.openaip.net/" className="font-bold hover:underline" style={{ color: 'var(--accent)' }}>
                    OpenAIP
                  </ExternalLink> · {t('flyingFields.communityDatabase')}
                </p>
              </div>
              <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                <span className="font-bold" style={{ color: 'var(--text)' }}>{t('flyingFields.baseMaps')}</span>
                <span className="ms-1">{t('flyingFields.baseMapsDesc')}</span>
                <p className="text-[10px] mt-0.5">{t('flyingFields.baseMaps3d')}</p>
              </div>
            </div>
          </details>

          {/* Flugplatz melden */}
          <ExternalLink href="https://ma-db.com/"
            className="flex items-center gap-3 rounded-2xl p-4 hover:opacity-80 transition-opacity min-w-[250px]"
            style={{ background: 'var(--bg-card)', border: '2px solid var(--accent)' }}>
            <span className="text-2xl">📍</span>
            <div>
              <p className="font-black text-sm" style={{ color: 'var(--accent)' }}>{t('flyingFields.reportField')}</p>
              <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{t('flyingFields.reportFieldDesc')}</p>
            </div>
            <span className="text-lg" style={{ color: 'var(--accent)' }}>→</span>
          </ExternalLink>
        </div>

        {/* Selektiertes Fluggebiet — Details */}
        {selectedField && (
          <div className="rounded-2xl p-6 mb-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-black" style={{ color: 'var(--text)' }}>
                  {selectedField.name}
                  {selectedField.verified && <span className="ms-2 text-sm" title={t('flyingFields.verified')}>✓</span>}
                </h2>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  {selectedField.lat.toFixed(5)}, {selectedField.lon.toFixed(5)}
                  {selectedField.altitude_m && ` · ${selectedField.altitude_m} m MSL`}
                  {selectedField.country && ` · ${selectedField.country}`}
                  {selectedField.icao_zone && ` · ${t('flyingFields.airspace')} ${selectedField.icao_zone}`}
                  {selectedField.airspace_ceiling_ft && ` · ${t('flyingFields.ceiling')} ${selectedField.airspace_ceiling_ft} ft`}
                </p>
              </div>
              <div className="text-sm px-3 py-1 rounded-full"
                   style={{ background: selectedField.icao_zone === 'G' ? '#22c55e20' : '#ef444420',
                            color: selectedField.icao_zone === 'G' ? '#22c55e' : '#ef4444' }}>
                {selectedField.icao_zone === 'G' ? t('flyingFields.uncontrolled') : `${t('flyingFields.airspace')} ${selectedField.icao_zone || '?'}`}
              </div>
            </div>

            {selectedField.description && (
              <p className="mt-3 text-sm" style={{ color: 'var(--muted)' }}>
                {selectedField.description}
              </p>
            )}

            {selectedField.rules_text && (
              <div className="mt-3 p-3 rounded-xl text-sm" style={{ background: 'var(--bg-primary)' }}>
                <strong>{t('flyingFields.siteRules')}</strong> {selectedField.rules_text}
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <ExternalLink href="https://www.dfs.de"
                 className="px-3 py-1.5 rounded-full text-xs font-bold"
                 style={{ background: 'var(--accent)', color: '#000' }}>
                {t('flyingFields.airspaceCheck')}
              </ExternalLink>
              {selectedField.website_url && (
                <ExternalLink href={selectedField.website_url}
                   className="px-3 py-1.5 rounded-full text-xs font-bold"
                   style={{ border: '1px solid var(--border)', color: 'var(--muted)' }}>
                  {t('flyingFields.website')}
                </ExternalLink>
              )}
            </div>
          </div>
        )}

        {/* Alte Fluggebiet-Liste entfernt — Daten kommen aus der Karte (Arno Bertz Layer) */}
      </div>
      <div className="px-4 py-3 text-center text-[11px] leading-relaxed" style={{ color: 'var(--muted)', borderTop: '1px solid var(--border)' }}>
        {t('mapDisclaimer.footerText')}
      </div>
    </div>}
    </div>
  )
}
